import React, { useState, useEffect, useRef } from 'react';
import './SearchSelect.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import validate from '../../common/validate';
import SimpleCheckbox from '../SimpleCheckbox/SimpleCheckbox';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';
import logComponent from '../../common/logComponent';

export default function SearchSelect({ disabled, displayField, errorMessage, instructions, itemComponent, itemComponentPropName, itemKeyField, itemPluralTerm, items, itemsError, itemsLoading, label, maximumSelections, name, onSelect, onValidate, potentialItemComponent, rules, searchFieldGroups, selectedItemComponent, selectedItems: selectedItemsProps, selectedItemsError, selectedItemsLoading, selectionMode, showError }) {
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(selectedItemsProps ? selectedItemsProps: []);
  const [showList, setShowList] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [validationDetails, setValidationDetails] = useState(null);
  const [isValid, setIsValid] = useState(null);
  const [needsValidation, setNeedsValidation] = useState(false);
  const [needsHandleSelectionChange, setNeedsHandleSelectionChange] = useState(false);
  const inputRef = useRef(null);
  const setInputRef = el => { inputRef.current = el; }

  const overlayRef = useRef(null);
  const selectedItemsRef = useRef(selectedItemsProps ? selectedItemsProps : []);
  const isValidating = useRef(false);

  const setSelectedItemsRef = el => {
    selectedItemsRef.current = el;
    setSelectedItems(el);
  }

  const setOverlayRef = el => { overlayRef.current = el; }

  if (logComponent('SearchSelect')) { console.debug('SearchSelect rendered'); }

  useEffect(() => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect onMount'); }
    validateThis();
  }, [])

  useEffect(() => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect useEffect [needsValidation]'); }
    if (needsValidation === true) {
      validateThis();
      setNeedsValidation(false);
    }
  }, [needsValidation])

  useEffect(() => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect useEffect [needsHandleSelectionChange]'); }
    if (needsHandleSelectionChange === true) {
      handleSelectionChange();
      setNeedsHandleSelectionChange(false);
    }
  }, [needsHandleSelectionChange])

  useEffect(() => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect useEffect [selectedItemsLoading]'); }
    // handle change in selectedItems loading state or 
    if (!selectedItemsLoading) return;
    if (!selectedItemsError) {
      setSelectedItemsRef(selectedItemsProps.map(item => item));
      setNeedsValidation(true);
    }
  }, [selectedItemsLoading])

  useEffect(() => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect useEffect [selectedItemsProps]'); }
    if (!selectedItemsProps) return;
    setSelectedItemsRef(selectedItemsProps.map(item => item));
    setNeedsValidation(true);
  }, [selectedItemsProps])


  useEffect(() => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect useEffect [inputValue]'); }
    // handle input value change
    handleInputValueChange();
  }, [inputValue])

  useEffect(() => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect useEffect [selectedItems]'); }
    if (selectionMode !== 'limit') {
      setFilteredItems(filteredItems.filter(item => !selectedItemsRef.current.some(selectedItem => selectedItem === item)));
    }
  }, [selectedItems])


  const handleInputValueChange = () => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect handleInputValueChange'); }
    if (inputValue.length < 2) {
      setShowList(false);
      setFilteredItems([]);
    } else {
      let newFilteredItems;
      if (!items) {
        newFilteredItems = [];
      } else {
        newFilteredItems = items.filter(item => {

          if (selectionMode !== 'limit' && 
            selectedItemsRef.current.some(selectedItem => selectedItem[itemKeyField] === item[itemKeyField])) return false;

          let textsToSearch = []
          searchFieldGroups.forEach(group => {
            let text = ''
            group.forEach(field => {
              let value = readNestedField(item, field);

              if (value) text += value + ' ';
            });

            textsToSearch.push(text);
          });

          // Return true for items that matches at least one search text
          return textsToSearch.some(text => text.toLowerCase().includes(inputValue.toLowerCase()));
        })
      }
      setFilteredItems(newFilteredItems);
      setShowList(true);
    } 

    if (overlayRef.current) overlayRef.current.scrollTo(0,0);
  }

  const readNestedField = (obj, nestedField) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect readNestedField'); }
    const nestedFieldArray = nestedField.split('.');

    if (!nestedFieldArray) return null;

    return nestedFieldArray.reduce((prev, curr) => {
      return prev[lowerCaseFirstLetter(curr)];
    }, obj);

  }

  const handleKeyDown = (e) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect handleKeyDown'); }
    const { key } = e;

    if (key && key == 'Escape') {
      setShowList(false);
    }
  }

  const clearInput = () => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect clearInput'); }
    setInputValue('');

    inputRef.current.focus();
  }

  const updateValue = (e) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect updateValue'); }
    setInputValue(e.target.value);
  }

  const addItem = (e) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect addItem'); }
    const { itemUniqueKey } = e.currentTarget.dataset;

    // if no item with this key is already selected, then add it to selectedItems array 
    if (!selectedItemsRef.current.some(item => item[itemKeyField] === itemUniqueKey)) {
      setSelectedItemsRef(selectedItemsRef.current.concat(filteredItems.find(item => item[itemKeyField] == itemUniqueKey)));// eslint-disable-line
      setShowList(false);
      setInputValue('');
      setNeedsHandleSelectionChange(true);
    }
  }

  const removeItem = (e) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect removeItem'); }
    const { itemUniqueKey } = e.currentTarget.dataset;

    setSelectedItemsRef(selectedItemsRef.current.filter(item => item[itemKeyField] != itemUniqueKey));// eslint-disable-line
    setNeedsHandleSelectionChange(true);
  }

  const handleSelectionChange = () => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect handleSelectionChange'); }

    if (onSelect) onSelect(selectedItemsRef.current);  

    validateThis();
  }

  const isSelected = (item) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect isSelected'); }

    return selectedItems.some(selectedItem => selectedItem === item);
  }

  const validateThis = () => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect validateThis'); }
    if (isValidating.current) return;
    isValidating.current = true;

    let validationDetailsThis = [];
    for (let rule in rules) {
      validationDetailsThis.push({
        rule: rule,
        valid: validate[rule](selectedItemsRef.current, rules[rule])
      })
    }

    let isValidThis = validationDetailsThis.reduce((prev, curr) => prev && curr.valid, true);

    setValidationDetails(validationDetailsThis);
    setIsValid(isValidThis);

    onValidate({
      name: name,
      isValid: isValidThis,
      value: selectedItemsRef.current
    });
    isValidating.current = false;
  }

  const toggleItem = (item) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect toggleItem'); }
    const { name: itemName } = item;

    if (item.selected) {
      setSelectedItemsRef(selectedItemsRef.current.concat(items.find(item => item[itemKeyField] === itemName)));
      setNeedsHandleSelectionChange(true);
    } else {
      setSelectedItemsRef(selectedItemsRef.current.filter(item => item[itemKeyField] !== itemName));
      setNeedsHandleSelectionChange(true);
    }
  }

  const toggleLimitItem = (e) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect toggleLimitItem'); }
    const { itemUniqueKey } = e.currentTarget.dataset;

    if (!selectedItemsRef.current.some(item => item[itemKeyField] == itemUniqueKey)) {
      setSelectedItemsRef(selectedItemsRef.current.concat(items.find(item => item[itemKeyField] == itemUniqueKey)));
    } else {
      setSelectedItemsRef(selectedItemsRef.current.filter(item => item[itemKeyField] != itemUniqueKey));
    }
    setInputValue('');
    setNeedsHandleSelectionChange(true);
  }

  const getPotentialItemComponent = (item) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect getPotentialItemComponent'); }

    const PotentialItemComponent = potentialItemComponent ? potentialItemComponent : itemComponent;

    if (PotentialItemComponent) {
      let itemComponentProp = {};
      itemComponentProp[itemComponentPropName] = item;

      return <PotentialItemComponent {...itemComponentProp} />;
    } else if (displayField && item[displayField]) {
      return <p className="Search-Select__generic-display-item">{ item[displayField] }</p>;
    } else {
      return null;
    }
  }

  const getSelectedItemComponent = (item) => {
    if (logComponent('SearchSelect')) { console.debug('SearchSelect getSelectedItemComponent'); }

    const SelectedItemComponent = selectedItemComponent ? selectedItemComponent : itemComponent;

    if (SelectedItemComponent) {
      let itemComponentProp = {};
      itemComponentProp[itemComponentPropName] = item;

      return <SelectedItemComponent {...itemComponentProp} />;
    } else if (displayField && item[displayField]) {
      return <p className="Search-Select__generic-display-item">{ item[displayField] }</p>;
    } else {
      return null;
    }    
  }

  const SelectedItemComponent = (selectedItemComponent ? selectedItemComponent : itemComponent);
  const PotentialItemComponent = (potentialItemComponent ? potentialItemComponent : itemComponent);

  const limitItemsToDisplay = inputValue.length >= 2 ? filteredItems : items;

  return (
    <div className="Search-Select">
      {
        showError && isValid === false &&
        <p className="Select-Input-Group__error">{errorMessage}</p>
      }
      <label
        className="Search-Select__label"
        htmlFor={name + '-multi-select'} >
        {label}
        {
          rules && rules.required &&
          <span className="Search-Select__required"> *</span>
        }
      </label>
      <div
        className="Search-Select__body">
        {
          !itemsLoading && !itemsError &&
          selectionMode === 'search' &&
          <React.Fragment>
            <div className="Search-Select__input-wrapper">
              <FontAwesomeIcon className="Search-Select__input-icon" icon="search" />
              <input
                placeholder={!maximumSelections || maximumSelections > selectedItems.length ? 'Search ' + itemPluralTerm : `Maximum number of ${itemPluralTerm} selected.`}
                readOnly={maximumSelections && maximumSelections <= selectedItems.length}
                className={'Search-Select__input' +
                  (showList ? ' Search-Select__input--visible' : '') +
                  (maximumSelections && maximumSelections <= selectedItems.length ? ' Search-Select__input-disabled' : '')}
                value={inputValue}
                onChange={updateValue}
                onFocus={handleInputValueChange}
                onKeyDown={handleKeyDown}
                ref={setInputRef} />
            </div>
            <ul
              ref={setOverlayRef}
              className={'Search-Select__selection-overlay' + (showList ? '--visible' : '')}>
              {
                filteredItems.length === 0 ?
                  <li key="noResults" className="Search-Select__empty-message">No matches found...</li> :
                  filteredItems.map(item => {
                    return <li
                      className="Search-Select__potential-item"
                      key={item[itemKeyField]}>
                      {getPotentialItemComponent(item)}
                      <button
                        tabIndex="-1"
                        type="button"
                        className={'Search-Select__add-item-btn' + (isSelected(item) ? ' Search-Select__add-item-btn--selected' : '')}
                        onClick={addItem}
                        data-item-unique-key={item[itemKeyField]}>
                        <FontAwesomeIcon icon="plus" />
                      </button>
                    </li>
                  })
              }
            </ul>
            {
              selectedItems.length > 0 &&
              <ul className="Search-Select__selected-items">
                {
                  items
                    .filter(item => selectedItems.some(selectedItem => selectedItem[itemKeyField] === item[itemKeyField]))
                    .map(item => {
                      return <li
                        className="Search-Select__selected-item"
                        key={item[itemKeyField]}>
                        {getSelectedItemComponent(item)}
                        <button
                          type="button"
                          className="Search-Select__remove-item-btn"
                          onClick={removeItem}
                          data-item-unique-key={item[itemKeyField]}>
                          <FontAwesomeIcon icon="times" />
                        </button>
                      </li>
                    })
                }
              </ul>
            }
          </React.Fragment>
        }
        {
          !itemsLoading && !itemsError && items &&
          selectionMode === 'list' &&
          <ul className="Search-Select__checkable-items">
            {
              items.length === 0 ?
                <li key="noResults" className="Search-Select__empty-message">No {itemPluralTerm} found...</li> :
                items.map(item => {
                  return <li
                    className="Search-Select__checkable-item"
                    key={item[itemKeyField]}>
                    {getSelectedItemComponent(item)}
                    <SimpleCheckbox
                      label=""
                      selected={selectedItems.some(selectedItem => selectedItem[itemKeyField] === item[itemKeyField])}
                      name={item[itemKeyField]}
                      toggleSelection={toggleItem}
                      validate={validateThis}
                      itemKey={item[itemKeyField]} />
                  </li>
                })
            }
          </ul>
        }
        {
          !itemsLoading && !itemsError && items &&
          selectionMode === 'limit' &&
          <React.Fragment>
            <div className="Search-Select__input-wrapper">
              <FontAwesomeIcon className="Search-Select__input-icon" icon="search" />
              <input
                placeholder={!maximumSelections || maximumSelections > selectedItems.length ? 'Search ' + itemPluralTerm : `Maximum number of ${itemPluralTerm} selected.`}
                readOnly={maximumSelections && maximumSelections <= selectedItems.length}
                className={'Search-Select__limit-input' +
                  (showList ? ' Search-Select__input--visible' : '') +
                  (maximumSelections && maximumSelections <= selectedItems.length ? ' Search-Select__input-disabled' : '')}
                value={inputValue}
                onChange={updateValue}
                onFocus={handleInputValueChange}
                onKeyDown={handleKeyDown}
                ref={setInputRef} />
            </div>
            <ul
              /* ref={setOverlayRef} */
              className="Search-Select__limit-items">
              {
                limitItemsToDisplay.length === 0 ?
                  <li key="noResults" className="Search-Select__empty-message">No matches found...</li> :
                  limitItemsToDisplay.map(item => {

                    const itemIsSelected = isSelected(item);

                    return <li
                      className="Search-Select__potential-item"
                      key={item[itemKeyField]}>
                      {getPotentialItemComponent(item)}
                      <button
                        tabIndex="-1"
                        type="button"
                        className={'Search-Select__toggle-item-btn' + (itemIsSelected ? ' Search-Select__toggle-item-btn--selected' : '')}
                        onClick={toggleLimitItem}
                        data-item-unique-key={item[itemKeyField]}>
                        <FontAwesomeIcon icon={itemIsSelected ? 'minus' : 'plus'} />
                      </button>
                    </li>
                  })
              }
            </ul>
            {
              selectedItems.length > 0 &&
              <React.Fragment>
                <p className="Search-Select__sub-label">Selected reason(s)</p>
                <ul className="Search-Select__selected-limit-items">
                  {
                    items
                      .filter(item => selectedItems.some(selectedItem => selectedItem[itemKeyField] === item[itemKeyField]))
                      .map(item => {
                        return <li
                          className="Search-Select__selected-item"
                          key={item[itemKeyField]}>
                          {getSelectedItemComponent(item)}
                          <button
                            type="button"
                            className="Search-Select__remove-item-btn"
                            onClick={removeItem}
                            data-item-unique-key={item[itemKeyField]}>
                            <FontAwesomeIcon icon="times" />
                          </button>
                        </li>
                      })
                  }
                </ul>
              </React.Fragment>
            }
          </React.Fragment>
        }
        {
          itemsLoading &&
          <p className="Search-Select__loading-message">Loading {itemPluralTerm}...</p>
        }
        {
          itemsError &&
          <p className="Search-Select__error-message">There was an error loading the {itemPluralTerm}.</p>
        }
      </div>
      <p className="Search-Select__instructions">{instructions}</p>
    </div>
  );
}